import axios from "axios";
import { defineStore } from "pinia";
import { computed, ref, watch } from "vue";
import { useAsyncOperation } from "Admin/js/Composables/useAsyncOperation";
import utilities from "Admin/js/Helpers/utilities";
import { useAppState } from "Admin/js/Stores/AppState";
import { useAuthorityLevelStore } from "Admin/js/Stores/AuthorityLevel";
import {
    DIVISION_TYPE,
    DIVISION_TYPE_PLACEHOLDER,
    useDivisionStore,
} from "Admin/js/Stores/Division";
import { useChat } from "Admin/js/Components/Chat/Store/Chat";
import { useTeamPanel } from "Admin/js/Stores/TeamPanel";
import { useUserStore } from "Admin/js/Stores/User";
import { useWorkstationConnectStore } from "Admin/js/Stores/WorkstationConnect";
import { profileRepresentsOrganizationOwner } from "Admin/js/Helpers/organizationOwnerProfile";

// =============================================================================
// Module helpers — division tree & headed-divisions (profile)
// =============================================================================

/**
 * @param {string | null | undefined} t - Division `type` from tree or headed row
 * @returns {boolean}
 */
function isExecutiveOfficeType(t) {
    return t === DIVISION_TYPE.EXECUTIVE_OFFICE || t === "EXECUTIVE_OFFICE";
}

/**
 * Whether a single headed-division row represents the Executive Office division.
 *
 * @param {object | null | undefined} row - One element from `headed_divisions`
 * @returns {boolean}
 */
function isHeadedRowExecutiveOffice(row) {
    if (!row || typeof row !== "object") {
        return false;
    }
    return isExecutiveOfficeType(row.type);
}

/**
 * Whether a headed-division `type` value means a normal division head (not EO / sub).
 *
 * @param {string | null | undefined} t
 * @returns {boolean}
 */
function headedTypeIsDivisionHead(t) {
    return (
        t == null ||
        t === DIVISION_TYPE.DIVISION ||
        t === "DIVISION" ||
        t === "division"
    );
}

/**
 * Whether a headed-division `type` value is sub-division head.
 *
 * @param {string | null | undefined} t
 * @returns {boolean}
 */
function headedTypeIsSubDivisionHead(t) {
    return (
        t === DIVISION_TYPE.SUB_DIVISION ||
        t === "SUB_DIVISION" ||
        t === "sub-division" ||
        t === "sub_division"
    );
}

/**
 * All division ids the user is a head of (any type), deduped, preserving ids as returned.
 * Used to merge locked divisions into selection and save payload.
 *
 * @param {object | null | undefined} p - User profile
 * @returns {Array<*>}
 */
function extractHeadedDivisionIdsFromProfile(p) {
    if (!p || typeof p !== "object") {
        return [];
    }
    const raw = p.headed_divisions ?? p.headedDivisions;
    if (!Array.isArray(raw)) {
        return [];
    }
    const out = [];
    const seen = new Set();
    for (const row of raw) {
        const id = row?.id;
        if (id == null || id === "") {
            continue;
        }
        const n = Number(id);
        if (!Number.isFinite(n) || seen.has(n)) {
            continue;
        }
        seen.add(n);
        out.push(id);
    }
    return out;
}

/**
 * Resolves a designation catalog row from API detail: prefer `designation_id`,
 * else match by trimmed title. Used when hydrating title + seniority from server.
 *
 * @param {Array<object>} designations - Loaded designations from authority store
 * @param {object} detail - Profile detail
 * @returns {object | undefined}
 */
function findDesignationRowForDetail(designations, detail) {
    if (!detail || !designations.length) {
        return undefined;
    }
    const id = detail.designation_id ?? detail.designation?.id;
    if (id != null && id !== "") {
        const byId = designations.find((d) => d.id == id);
        if (byId) {
            return byId;
        }
    }
    const needle = (detail.title || detail.designation?.title || "")
        .toString()
        .trim();
    if (!needle) {
        return undefined;
    }
    return designations.find(
        (d) => (d.title ?? "").toString().trim() === needle,
    );
}

// =============================================================================
// Store: Chat / Worker Settings — selected associate’s divisions, title, seniority, reports
// =============================================================================

export const useAssociateStore = defineStore("associate", () => {
    const { manageAsyncOperation, isLoading: isWorkerManageSaving } =
        useAsyncOperation();

    const authorityLevelStore = useAuthorityLevelStore();
    const divisionStore = useDivisionStore();
    const userStore = useUserStore();
    const appStateStore = useAppState();
    const selectedSeniorityLevel = ref(null);
    const selectedDivisionIds = ref([]);
    const selectedDesignation = ref(null);
    const selectedWscMandate = ref(false);

    const userServerFetchedSnapshot = ref({
        seniorityLevel: null,
        divisionIds: [],
        designationTitle: null,
        reportsToProfileId: null,
        isForceAppShift: false,
    });

    const showDivisionMappingConfirm = ref(false);
    const showSaveChangesConfirm = ref(false);
    const pendingNavigationCallback = ref(null);

    function showSaveChangesConfirmDialog(callback = null) {
        pendingNavigationCallback.value = callback ?? null;
        showSaveChangesConfirm.value = true;
    }

    function cancelSaveChangesConfirmDialog() {
        showSaveChangesConfirm.value = false;
    }

    function dismissSaveChangesConfirmDialog() {
        showSaveChangesConfirm.value = false;
        pendingNavigationCallback.value = null;
    }

    async function confirmSaveChangesConfirmDialog() {
        showSaveChangesConfirm.value = false;
        pendingNavigationCallback.value = null;
    }

    function callAndClearPendingNavigationCallback() {
        const cb = pendingNavigationCallback.value;
        pendingNavigationCallback.value = null;
        cb?.();
    }

    function runPendingActionAfterAssociateSaveDialogIfNeeded(callback) {
        const chatStore = useChat();
        if (
            chatStore.is_worker_settings_tab &&
            hasWorkerFormDirtyChanges.value
        ) {
            showSaveChangesConfirmDialog(callback);
            return;
        }
        callback?.();
    }

    // -------------------------------------------------------------------------
    // Catalog → dropdown option shapes & title string for API
    // -------------------------------------------------------------------------

    /**
     * Plain title string sent to the manage API and stored in the dirty snapshot
     * (designation catalog title, or custom free-typed label).
     *
     * @param {object | null | undefined} opt - `selectedDesignation` option shape
     * @returns {string | null}
     */
    const managePayloadTitle = (opt) =>
        (opt?.raw?.title ?? "").toString().trim() ||
        (opt?.label ?? "").toString().trim() ||
        null;

    /**
     * Maps a designation row from the authority store into the Title dropdown option shape.
     *
     * @param {object} d - Designation row
     */
    const designationRowToOption = (d) => {
        return {
            label: d.title,
            value: d.id ?? null,
            raw: d,
            listLabel: d.designation_with_authority_level,
        };
    };

    /**
     * Maps an authority level row into the Seniority dropdown option shape.
     *
     * @param {object} al - Authority level row
     */
    const seniorityRowToOption = (al) => ({
        label: (al?.name ?? "").toString().trim(),
        value: al.id,
        raw: al,
    });

    /** In-flight `hydrateWorkerSettingsForm` promises keyed by profile id (dedupe concurrent hydrates). */
    const hydrateWorkerSettingsPromisesByProfileId = new Map();

    // -------------------------------------------------------------------------
    // Core computeds — who is being edited & catalog-driven options
    // -------------------------------------------------------------------------

    /** Profile of the associate currently selected in worker settings (by `selectedProfileId`). */
    const profile = computed(() =>
        userStore.getUserProfileWithCountryId(
            authorityLevelStore.selectedProfileId,
        ),
    );

    /** True if the viewed associate owns the org (affects reports-to and save payload). */
    const isViewedAssociateOrgOwner = computed(() =>
        profileRepresentsOrganizationOwner(
            profile.value,
            userStore.profile?.organization,
        ),
    );

    /**
     * Stable string of sorted headed division ids; used in `watch` so heading changes
     * re-run division lock sync without deep-watching the whole profile object.
     */
    const headedDivisionsLockSignature = computed(() =>
        extractHeadedDivisionIdsFromProfile(profile.value)
            .map((id) => Number(id))
            .filter((n) => Number.isFinite(n))
            .sort((a, b) => a - b)
            .join(","),
    );

    /** Resolved “reports to” user profile object for the Reports-to picker, if any. */
    const reportsToProfile = computed(() =>
        authorityLevelStore.reportsToProfileId
            ? userStore.getUserProfileById(
                  authorityLevelStore.reportsToProfileId,
              )
            : null,
    );

    /** Seniority dropdown options from loaded authority levels. */
    const seniorityLevelOptions = computed(() =>
        authorityLevelStore.authorityLevels.map(seniorityRowToOption),
    );

    /**
     * Division multi-select options for `DivisionBox`:
     * - Top-line only: direct children of the org division root (no nested division/sub-division rows).
     * - Org-owner associates always see Executive Office, even when top-line divisions are empty.
     * - Division heads: checked+disabled (`is_division_head`).
     * - `has_sub_division_head`: user heads a sub-division under this division (parent lock), or heads
     *   this row when it is the sub-division (same flag for both cases when the row appears).
     * - If the associate heads EO, Executive Office is prepended as a locked row.
     * - `is_owner` on the Executive Office row when the associate is org owner or heads EO.
     * - Executive Office is always first; remaining divisions are sorted by label.
     */

    const isExecutiveOfficeHead = computed(() => {
        const isExecutive = profile.value?.headed_divisions?.find((item) => {
            return item.type === "EXECUTIVE_OFFICE";
        });
        return Boolean(isExecutive);
    });

    const divisionsOption = computed(() => {
        const divisions = divisionStore.divisions?.children ?? [];

        function collectHeadProfiles(node) {
            const profiles = [];
            if (node.headUserProfileId) {
                profiles.push({
                    id: node.headUserProfileId,
                    name: node.label || node.name || null,
                });
            }
            for (const child of node.children || []) {
                profiles.push(...collectHeadProfiles(child));
            }
            return profiles;
        }

        const executive = {
            id: divisionStore.divisions?.trackId,
            name:
                divisionStore.divisions?.name ||
                DIVISION_TYPE_PLACEHOLDER[DIVISION_TYPE.EXECUTIVE_OFFICE],
            label:
                divisionStore.divisions?.label ||
                DIVISION_TYPE_PLACEHOLDER[DIVISION_TYPE.EXECUTIVE_OFFICE],
            value: divisionStore.divisions?.trackId,
            is_owner: true,
            disable: true,
        };

        const otherDivisions = divisions.map((division) => {
            const currentUserProfileId = profile.value?.user_profile_id;
            const headedProfiles = collectHeadProfiles(division);
            const currentHeadProfile = headedProfiles.find(
                (headProfile) => headProfile.id === currentUserProfileId,
            );
            const isSubDivisionHead = Boolean(currentHeadProfile);
            const mapped = {
                id: division.trackId,
                name: division.name,
                label: division.label,
                divisionHead:
                    division.headUserProfileId === currentUserProfileId,
                value: division.trackId,
                subDivisionHead: isSubDivisionHead,
                subDivisionName: currentHeadProfile?.name ?? null,
                disable:
                    division.headUserProfileId === currentUserProfileId ||
                    isSubDivisionHead,
            };

            return mapped;
        });

        if (isExecutiveOfficeHead.value) {
            return [
                executive,
                ...otherDivisions.sort((a, b) =>
                    a.label.localeCompare(b.label, undefined, {
                        numeric: true,
                    }),
                ),
            ];
        }

        return otherDivisions.sort((a, b) =>
            a.label.localeCompare(b.label, undefined, {
                numeric: true,
            }),
        );
    });

    /** Title dropdown options from loaded designations. */
    const designationOptions = computed(() =>
        authorityLevelStore.designations.map(designationRowToOption),
    );

    // -------------------------------------------------------------------------
    // Form validation & dirty detection
    // -------------------------------------------------------------------------
    /**
     * Compares two division id arrays as sets (order-insensitive).
     *
     * @param {Array<*>} a
     * @param {Array<*>} b
     */
    const divisionIdsEqual = (a, b) => {
        if (a.length !== b.length) {
            return false;
        }
        const sortedA = [...a].sort();
        const sortedB = [...b].sort();
        return sortedA.every((val, i) => val == sortedB[i]);
    };

    /** Workstation Connect mandate toggle differs from last hydrated snapshot. */
    const hasWscMandateChanged = computed(
        () =>
            userServerFetchedSnapshot.value.isForceAppShift !==
            selectedWscMandate.value,
    );

    /** True if any worker field differs from `userServerFetchedSnapshot` (divisions, title, seniority, reports, WSC). */
    const hasWorkerFormDirtyChanges = computed(() => {
        const snapshotSeniorityId =
            userServerFetchedSnapshot.value.seniorityLevel ?? null;
        const currentSeniorityId = selectedSeniorityLevel.value?.value ?? null;
        const isSeniorityLevelChanged =
            snapshotSeniorityId !== currentSeniorityId;

        const isDivisionIdsChanged = !divisionIdsEqual(
            userServerFetchedSnapshot.value.divisionIds,
            selectedDivisionIds.value,
        );

        const currentTitle = (
            managePayloadTitle(selectedDesignation.value) || ""
        )
            .toString()
            .trim();
        const snapshotTitle = (
            userServerFetchedSnapshot.value.designationTitle || ""
        )
            .toString()
            .trim();
        const isDesignationChanged = currentTitle !== snapshotTitle;

        const isReportsToProfileIdChanged =
            !isViewedAssociateOrgOwner.value &&
            userServerFetchedSnapshot.value.reportsToProfileId !=
                authorityLevelStore.reportsToProfileId;

        return (
            isSeniorityLevelChanged ||
            isDivisionIdsChanged ||
            isDesignationChanged ||
            isReportsToProfileIdChanged ||
            hasWscMandateChanged.value
        );
    });

    /** Save allowed only when divisions, title, seniority, and reports-to (if required) are filled. */
    const hasRequiredWorkerFieldsComplete = computed(() => {
        const hasDivision =
            Array.isArray(selectedDivisionIds.value) &&
            selectedDivisionIds.value.length > 0;

        const hasTitle = Boolean(managePayloadTitle(selectedDesignation.value));

        const hasSeniority = selectedSeniorityLevel.value != null;

        const reportsToOk =
            isViewedAssociateOrgOwner.value ||
            (authorityLevelStore.reportsToProfileId != null &&
                authorityLevelStore.reportsToProfileId !== "");

        return hasDivision && hasTitle && hasSeniority && reportsToOk;
    });

    /** Current user must be owner or admin to persist worker settings. */
    const canCurrentUserSaveWorkerSettings = computed(() =>
        Boolean(userStore.isOwnerOrAdmin),
    );

    /** Disabled when missing permission, incomplete fields, or no edits vs snapshot. */
    const isSaveButtonDisabled = computed(
        () =>
            !canCurrentUserSaveWorkerSettings.value ||
            !hasRequiredWorkerFieldsComplete.value ||
            !hasWorkerFormDirtyChanges.value,
    );

    /** Divisions alone changed vs snapshot (e.g. for partial UI that only cares about division mapping). */
    const hasDivisionMappingChanged = computed(
        () =>
            !divisionIdsEqual(
                userServerFetchedSnapshot.value.divisionIds,
                selectedDivisionIds.value,
            ),
    );

    // -------------------------------------------------------------------------
    // Division locks (watch + hydrate) & save payload shape
    // `normalizedDivisionIdsForSave` → `buildManagePayload` / save
    // -------------------------------------------------------------------------

    /**
     * Ensures every headed division id (including EO) is present in `selectedDivisionIds`
     * so locked rows stay selected in the UI and on save.
     */
    function syncHeadedDivisionsInSelection() {
        const headedIds = extractHeadedDivisionIdsFromProfile(profile.value);
        if (headedIds.length === 0) {
            return;
        }
        const existing = new Set(selectedDivisionIds.value.map(Number));
        const toAdd = [];
        for (const id of headedIds) {
            const n = Number(id);
            if (!existing.has(n)) {
                existing.add(n);
                toAdd.push(id);
            }
        }
        if (toAdd.length > 0) {
            selectedDivisionIds.value = [
                ...selectedDivisionIds.value,
                ...toAdd,
            ];
        }
    }

    /**
     * Entry point for keeping division locks in sync (called from `watch` and hydrate).
     * Currently forwards to `syncHeadedDivisionsInSelection`.
     */
    function syncLockedDivisionSelections() {
        syncHeadedDivisionsInSelection();
    }

    /**
     * Builds the `divisions` array for the manage API: current selection plus any
     * headed division ids not yet in the selection (cannot drop locked divisions).
     * Deduplicates by numeric id while preserving first-seen order.
     *
     * @returns {Array<number|string>}
     */
    function normalizedDivisionIdsForSave() {
        let raw = [...(selectedDivisionIds.value ?? [])];
        const headed = extractHeadedDivisionIdsFromProfile(profile.value);
        const nums = new Set(raw.map(Number));
        for (const id of headed) {
            const n = Number(id);
            if (!nums.has(n)) {
                nums.add(n);
                raw.push(id);
            }
        }
        const seen = new Set();
        const deduped = [];
        for (const id of raw) {
            const n = Number(id);
            if (seen.has(n)) {
                continue;
            }
            seen.add(n);
            deduped.push(id);
        }
        return deduped;
    }

    // -------------------------------------------------------------------------
    // Hydrate pipeline — catalog, fetch detail, apply → refs, baseline snapshot, orchestrator
    // Order inside `hydrateWorkerSettingsForm`:
    //   refreshWorkerSettingsCatalog → fetchAndMergeProfileDetail →
    //   applyDivisionSelectionFromDetail → syncLockedDivisionSelections → applyReportsToFromDetail →
    //   (org owner clears reports) → applyDesignationAndSeniorityFromDetail → applyWscMandateFromDetail →
    //   captureUserServerFetchedSnapshot
    // -------------------------------------------------------------------------

    /**
     * Loads authority levels, designations, top-line divisions tree, and countries
     * before binding a worker profile into the form.
     */
    async function refreshWorkerSettingsCatalog() {
        await Promise.all([
            authorityLevelStore.fetchAllAuthorityLevels(),
            divisionStore.fetchAllDivisions({ noLoading: true }),
            authorityLevelStore.fetchAllDesignations(),
            appStateStore.loadCountries(),
        ]);
    }

    /** Clears local worker form state when closing or switching context. */
    function resetWorkerFormSelections() {
        console.log("resetWorkerFormSelections");
        selectedSeniorityLevel.value = null;
        selectedDesignation.value = null;
        selectedDivisionIds.value = [];
        selectedWscMandate.value = false;
        authorityLevelStore.setReportsToProfileId(null);
        // Keep locked headed divisions selected, then baseline snapshot to that state
        // so load-time sync does not look like unsaved user edits.
        syncLockedDivisionSelections();
        captureUserServerFetchedSnapshot();
    }

    /**
     * Fetches fresh profile by id, merges into the user store, and returns detail if
     * the user is still viewing the same profile (guards race on fast tab switches).
     *
     * @param {string|number} expectedProfileId
     * @returns {Promise<object | null>}
     */
    async function fetchAndMergeProfileDetail(expectedProfileId) {
        const detail = await userStore.getProfileById(expectedProfileId);
        if (!detail?.id) {
            return null;
        }
        if (authorityLevelStore.selectedProfileId !== expectedProfileId) {
            return null;
        }
        await userStore.replaceUserProfile(detail);
        if (authorityLevelStore.selectedProfileId !== expectedProfileId) {
            return null;
        }
        return detail;
    }

    /** Sets `selectedDivisionIds` from `division_profiles` on profile detail. */
    function applyDivisionSelectionFromDetail(detail) {
        const rows = detail.division_profiles;
        selectedDivisionIds.value = Array.isArray(rows)
            ? rows.map((dp) => dp.division_id)
            : [];
    }

    /** Pushes profile detail’s reports-to id into the authority store. */
    function applyReportsToFromDetail(detail) {
        authorityLevelStore.setReportsToProfileId(
            detail.direct_reports_to?.reports_to_profile_id ?? null,
        );
    }

    /**
     * Hydrates Title (`selectedDesignation`) and Seniority (`selectedSeniorityLevel`)
     * from API detail: prefers catalog designation match, supports embedded `designation`
     * object or free-text title fallback.
     *
     * @param {object} detail - Profile detail from `getProfileById`
     */
    function applyDesignationAndSeniorityFromDetail(detail) {
        const designations = authorityLevelStore.designations;
        let row = findDesignationRowForDetail(designations, detail);

        if (
            !row &&
            detail.designation &&
            typeof detail.designation === "object"
        ) {
            row = detail.designation;
        }

        const fallbackTitle =
            (row?.title ?? "").toString().trim() ||
            (detail.title ?? "").toString().trim() ||
            (detail.designation?.title ?? "").toString().trim();

        const hasCatalogRow = row && (row.id != null || fallbackTitle);

        if (hasCatalogRow) {
            const rowForOption =
                fallbackTitle && !(row.title ?? "").toString().trim()
                    ? { ...row, title: fallbackTitle }
                    : row;
            selectedDesignation.value = designationRowToOption(rowForOption);
            const aid =
                rowForOption.authority_level_id ??
                rowForOption.authorityLevelId;
            const levelRow = authorityLevelStore.authorityLevels.find(
                (al) => al.id === aid,
            );
            selectedSeniorityLevel.value = levelRow
                ? seniorityRowToOption(levelRow)
                : null;
            return;
        }

        selectedDesignation.value = fallbackTitle
            ? { label: fallbackTitle, value: null }
            : null;

        const meta = detail.designation;
        const aid =
            meta && typeof meta === "object"
                ? meta.authority_level_id ?? meta.authorityLevelId
                : null;
        let levelRow = null;
        if (aid != null) {
            levelRow = authorityLevelStore.authorityLevels.find(
                (al) => al.id === aid,
            );
        } else if (detail.authority_level != null) {
            levelRow = authorityLevelStore.authorityLevels.find(
                (al) => al.level == detail.authority_level,
            );
        }
        selectedSeniorityLevel.value = levelRow
            ? seniorityRowToOption(levelRow)
            : null;
    }

    /** Sets WSC mandate toggle from `detail.is_force_app_shift`. */
    function applyWscMandateFromDetail(detail) {
        selectedWscMandate.value = Boolean(detail?.is_force_app_shift);
    }

    /**
     * Copies current form state into `userServerFetchedSnapshot` so dirty checks
     * compare against the last loaded / post-save baseline.
     */
    function captureUserServerFetchedSnapshot() {
        userServerFetchedSnapshot.value = {
            seniorityLevel: selectedSeniorityLevel.value?.value ?? null,
            divisionIds: [...selectedDivisionIds.value],
            designationTitle: managePayloadTitle(selectedDesignation.value),
            reportsToProfileId: authorityLevelStore.reportsToProfileId,
            isForceAppShift: selectedWscMandate.value,
        };
    }

    /**
     * Reverts all local worker form selections (divisions, title, seniority, WSC, reports)
     * back to the baseline last captured in `userServerFetchedSnapshot`.
     */
    function revertWorkerFormToSnapshot() {
        const snapshot = userServerFetchedSnapshot.value;

        selectedDivisionIds.value = [...snapshot.divisionIds];
        selectedWscMandate.value = snapshot.isForceAppShift;
        authorityLevelStore.setReportsToProfileId(snapshot.reportsToProfileId);

        // Map seniority back from ID
        selectedSeniorityLevel.value =
            seniorityLevelOptions.value.find(
                (seniorityLevel) =>
                    seniorityLevel.value === snapshot.seniorityLevel,
            ) || null;

        // Map title back by matching current managePayloadTitle output
        const matchedDesignation = designationOptions.value.find(
            (designation) =>
                managePayloadTitle(designation) === snapshot.designationTitle,
        );
        if (matchedDesignation) {
            selectedDesignation.value = matchedDesignation;
        } else {
            selectedDesignation.value = snapshot.designationTitle
                ? { label: snapshot.designationTitle, value: null }
                : null;
        }
    }

    /**
     * Full load path for worker settings: refresh catalogs, fetch profile detail,
     * apply divisions / reports / designation / WSC, fix org-owner reports-to,
     * then snapshot for dirty tracking. Deduplicated per `selectedProfileId`.
     *
     * @returns {Promise<void>}
     */
    async function hydrateWorkerSettingsForm() {
        const expectedProfileId = authorityLevelStore.selectedProfileId;
        if (!expectedProfileId) {
            return;
        }

        const existing =
            hydrateWorkerSettingsPromisesByProfileId.get(expectedProfileId);
        if (existing) {
            return existing;
        }

        let runPromise;
        runPromise = (async () => {
            try {
                await refreshWorkerSettingsCatalog();
                if (
                    authorityLevelStore.selectedProfileId !== expectedProfileId
                ) {
                    return;
                }
                // TEMP: artificial delay to reproduce staging race locally — remove after verification
                // await new Promise((resolve) => setTimeout(resolve, 5000));
                const detail =
                    await fetchAndMergeProfileDetail(expectedProfileId);
                if (!detail) {
                    return;
                }
                applyDivisionSelectionFromDetail(detail);
                syncLockedDivisionSelections();
                applyReportsToFromDetail(detail);
                const mergedProfile =
                    userStore.getUserProfileById(expectedProfileId);
                const currentOrg = userStore.profile?.organization;
                const isOrgOwnerAssociate =
                    profileRepresentsOrganizationOwner(detail, currentOrg) ||
                    profileRepresentsOrganizationOwner(
                        mergedProfile,
                        currentOrg,
                    );
                if (isOrgOwnerAssociate) {
                    authorityLevelStore.setReportsToProfileId(null);
                }
                applyDesignationAndSeniorityFromDetail(detail);
                applyWscMandateFromDetail(detail);
                captureUserServerFetchedSnapshot();
            } finally {
                if (
                    hydrateWorkerSettingsPromisesByProfileId.get(
                        expectedProfileId,
                    ) === runPromise
                ) {
                    hydrateWorkerSettingsPromisesByProfileId.delete(
                        expectedProfileId,
                    );
                }
            }
        })();
        hydrateWorkerSettingsPromisesByProfileId.set(
            expectedProfileId,
            runPromise,
        );
        return runPromise;
    }

    // -------------------------------------------------------------------------
    // Persist worker settings
    // `saveWorkerManage` → `buildManagePayload` → `normalizedDivisionIdsForSave`
    // -------------------------------------------------------------------------

    /**
     * Payload body for `POST /user-profiles/:id/manage` (divisions deduped + headed locks, title, seniority, reports, WSC).
     *
     * @returns {object}
     */
    function buildManagePayload() {
        return {
            authority_level: selectedSeniorityLevel.value?.raw?.level ?? null,
            authority_level_id: selectedSeniorityLevel.value?.value ?? null,
            reports_to_profile_id: isViewedAssociateOrgOwner.value
                ? null
                : reportsToProfile.value?.id ?? null,
            divisions: normalizedDivisionIdsForSave(),
            title: managePayloadTitle(selectedDesignation.value),
            is_force_app_shift: selectedWscMandate.value,
        };
    }

    /**
     * After manage save: merge profile detail, refresh team hierarchy/dashboard, and associate dashboard when open.
     *
     * @param {string|number} savedProfileId
     */
    async function syncReportingHierarchyAfterManage(savedProfileId) {
        await fetchAndMergeProfileDetail(savedProfileId);

        const teamPanelStore = useTeamPanel();
        if (teamPanelStore.is_prepared) {
            await teamPanelStore.refreshReportingHierarchy();
        } else {
            useAppState().requestTeamDashboardRefresh();
        }

        const chat = useChat();
        const dashboardProfileId =
            chat.standalone_associate_profile_id ??
            authorityLevelStore.selectedProfileId;
        if (
            dashboardProfileId != null &&
            Number(dashboardProfileId) === Number(savedProfileId)
        ) {
            void chat.fetchAssociateDashboard(savedProfileId);
        }
    }

    /**
     * Persists worker settings via manage API; on success refreshes WSC users.
     */
    function saveWorkerManage() {
        return manageAsyncOperation(
            async () => {
                const savedProfileId = profile.value.id;
                const { data } = await axios.post(
                    `/user-profiles/${savedProfileId}/manage`,
                    buildManagePayload(),
                );
                utilities().showAssignmentSuccessSnackbar(
                    data?.message ?? "Worker settings updated successfully.",
                );
                void useWorkstationConnectStore().fetchWscUsers(false);
                await syncReportingHierarchyAfterManage(savedProfileId);
                captureUserServerFetchedSnapshot();
                return data.data;
            },
            (error) => {
                const {
                    getApiResponseErrorMessage,
                    showErrorSnackbar,
                    handleError,
                    getObjFlatArray,
                } = utilities();

                const errorMessages = getObjFlatArray(
                    error?.response?.data?.errors || {},
                );
                if (errorMessages.length > 0) {
                    showErrorSnackbar(errorMessages[0]);
                } else {
                    showErrorSnackbar(
                        getApiResponseErrorMessage(error) ??
                            "Something went wrong while saving worker settings.",
                    );
                }
                handleError(error, false);
            },
        );
    }

    // -------------------------------------------------------------------------
    // Side effects & UI-driven sync
    // -------------------------------------------------------------------------

    /**
     * When division selection or headed-division set changes, re-apply headed ids
     * into `selectedDivisionIds` so locked divisions stay selected.
     */
    watch(
        [selectedDivisionIds, headedDivisionsLockSignature],
        () => {
            syncLockedDivisionSelections();
        },
        { deep: true },
    );

    watch(
        selectedDesignation,
        () => {
            if (managePayloadTitle(selectedDesignation.value) == null) {
                selectedSeniorityLevel.value = null;
            }
        },
        { deep: true },
    );

    /**
     * Keep the Seniority selection object in sync when the catalog row changes (e.g. another user
     * renames a level via LiveSync). Options update from `authorityLevels`, but `selectedSeniorityLevel`
     * is a snapshot — refresh it so KSelect shows the new label without leaving Associate Settings.
     */
    watch(
        () => {
            const sel = selectedSeniorityLevel.value;
            if (sel?.value == null) {
                return null;
            }
            const row = authorityLevelStore.authorityLevels.find(
                (al) => al.id === sel.value,
            );
            if (!row) {
                return `missing:${sel.value}`;
            }
            return [row.name, row.level].join("\u001f");
        },
        () => {
            const sel = selectedSeniorityLevel.value;
            if (sel?.value == null) {
                return;
            }
            const row = authorityLevelStore.authorityLevels.find(
                (al) => al.id === sel.value,
            );
            if (row) {
                selectedSeniorityLevel.value = seniorityRowToOption(row);
            } else {
                selectedSeniorityLevel.value = null;
            }
        },
    );

    /**
     * Call when the user explicitly picks a Title row from the dropdown (not while typing).
     * Keeps Seniority aligned with the designation’s authority level without reacting to debounced input matches.
     */
    function syncSeniorityFromPickedDesignation() {
        const raw = selectedDesignation.value?.raw;
        if (!raw) {
            return;
        }
        const authorityLevelId = raw.authority_level_id ?? raw.authorityLevelId;
        if (authorityLevelId == null) {
            return;
        }
        const levelRow = authorityLevelStore.authorityLevels.find(
            (al) => al.id === authorityLevelId,
        );
        if (levelRow) {
            selectedSeniorityLevel.value = seniorityRowToOption(levelRow);
        }
    }

    return {
        showDivisionMappingConfirm,
        showSaveChangesConfirm,
        canCurrentUserSaveWorkerSettings,
        hasWorkerFormDirtyChanges,
        isSaveButtonDisabled,
        selectedSeniorityLevel,
        selectedDivisionIds,
        selectedDesignation,
        selectedWscMandate,
        profile,
        reportsToProfile,
        seniorityLevelOptions,
        designationOptions,
        isWorkerManageSaving,
        hasDivisionMappingChanged,
        hasWscMandateChanged,
        isViewedAssociateOrgOwner,
        resetWorkerFormSelections,
        refreshWorkerSettingsCatalog,
        hydrateWorkerSettingsForm,
        saveWorkerManage,
        syncSeniorityFromPickedDesignation,
        revertWorkerFormToSnapshot,
        divisionsOption,
        showSaveChangesConfirmDialog,
        cancelSaveChangesConfirmDialog,
        dismissSaveChangesConfirmDialog,
        confirmSaveChangesConfirmDialog,
        callAndClearPendingNavigationCallback,
        runPendingActionAfterAssociateSaveDialogIfNeeded,
    };
});
